import { useEffect, useMemo, useState } from 'react';
import {
  buildCalendarDays,
  buildEventDateMap,
  formatDateKey,
  formatTime,
  parseDate,
  parseEventNotes,
  startOfMonth,
} from '@car-pool/shared/calendar';
import { formatCalendarEventTiming } from '../lib/calendarEvents';
import { getEarliestEffectiveDismissalTime, getEarliestTimeOfDay } from '@car-pool/shared/dismissalTimes';
import { describeNextCarpool } from '@car-pool/shared/carpoolSchedule';
import {
  parseAssignedGradeSplitIds,
  parseAssignedGrades,
  parseChildrenIds,
} from '@car-pool/shared/schoolRecords';
import DismissalStateControl from '../components/DismissalStateControl';
import EmergencyLockdownModal from '../components/EmergencyLockdownModal';
import PickupStudentsCard from '../components/PickupStudentsCard';
import StaffGradeAssignModal from '../components/StaffGradeAssignModal';
import StudentParentMatchModal from '../components/StudentParentMatchModal';
import useSchoolDismissalLiveData from '../hooks/useSchoolDismissalLiveData';
import { isDismissalSimulationRunning } from '../lib/dismissalSimulation';
import SchoolAvatar from '../components/SchoolAvatar';
import SchoolLayout from '../components/SchoolLayout';
import { io } from 'socket.io-client';
import { apiFetch, SOCKET_URL } from '../config/api';
import { secondsUntilSchoolTime } from '../lib/schoolTime';


const QUICK_ACTIONS = [
  {
    key: 'start-dismissal',
    label: 'Start Dismissal',
    description: 'Begin student pickup and activate queue',
    iconSrc: '/icons/quick_actions/start_dismissal.png',
    span: 2,
  },
  {
    key: 'end-dismissal',
    label: 'End Dismissal',
    iconSrc: '/icons/quick_actions/end_dismissal.png',
    span: 2,
  },
  {
    key: 'emergency-lockdown',
    label: 'Emergency Lockdown',
    description: 'Immediately stop all activity and lock the system',
    iconSrc: '/icons/quick_actions/emergency_lockdown.png',
    span: 2,
  },
];

const DASHBOARD_WEEK_DAYS = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];
const WEEKDAY_CODES = ['SU', 'MO', 'TU', 'WE', 'TH', 'FR', 'SA'];
const REVIEW_CONTACT_STATUSES = ['NEEDS_REVIEW', 'FAILED'];

function normalizeReviewValue(value) {
  return String(value || '').trim().toLowerCase();
}

function normalizeReviewAddress(value) {
  return normalizeReviewValue(value).replace(/[^\w]+/g, ' ').replace(/\s+/g, ' ').trim();
}

function getReviewPayload(row) {
  return {
    ...(row?.best_payload || {}),
    ...(row?.manual_override_payload || {}),
  };
}

function getReviewAccountId(row) {
  return normalizeReviewValue(row?.account_id || getReviewPayload(row).__account_id);
}

function getStudentReviewAccountIds(student, studentRows) {
  if (!student) return new Set();

  const studentId = Number(student.id);
  const sisStudentId = normalizeReviewValue(student.sis_student_id);

  return new Set(
    studentRows
      .filter((row) => {
        const payload = getReviewPayload(row);
        if (Number(row.live_record_id) === studentId) return true;
        return sisStudentId && normalizeReviewValue(payload.sis_student_id) === sisStudentId;
      })
      .map(getReviewAccountId)
      .filter(Boolean)
  );
}

function buildQueuedContactMatches(student, contactRows, studentRows) {
  if (!student) return [];

  const accountIds = getStudentReviewAccountIds(student, studentRows);
  const studentLastName = normalizeReviewValue(student.l_name);
  const studentAddress = normalizeReviewAddress(student.street_address);

  return contactRows
    .map((row) => {
      const payload = getReviewPayload(row);
      const accountId = getReviewAccountId(row);
      const reasons = [];

      if (accountId && accountIds.has(accountId)) {
        reasons.push('same import account');
      }

      const contactLastName = normalizeReviewValue(payload.l_name);
      const contactAddress = normalizeReviewAddress(payload.street_address);
      if (
        studentLastName
        && studentAddress
        && contactLastName === studentLastName
        && contactAddress === studentAddress
      ) {
        reasons.push('same last name and address');
      }

      if (!reasons.length) return null;

      return {
        ...row,
        match_reasons: reasons,
      };
    })
    .filter(Boolean)
    .sort((left, right) => {
      const leftExact = left.match_reasons?.includes('same import account') ? 0 : 1;
      const rightExact = right.match_reasons?.includes('same import account') ? 0 : 1;
      if (leftExact !== rightExact) return leftExact - rightExact;
      return (Number(left.source_row) || Number(left.id) || 0) - (Number(right.source_row) || Number(right.id) || 0);
    });
}

function buildDashboardScheduleGroups(events, monthDate) {
  const groups = new Map();

  events.forEach((event) => {
    const eventDate = parseDate(event.start_date);
    if (!eventDate) return;
    if (eventDate.getFullYear() !== monthDate.getFullYear() || eventDate.getMonth() !== monthDate.getMonth()) {
      return;
    }

    const dateKey = formatDateKey(eventDate);
    if (!groups.has(dateKey)) {
      groups.set(dateKey, []);
    }
    groups.get(dateKey).push(event);
  });

  return [...groups.entries()]
    .sort(([left], [right]) => left.localeCompare(right))
    .slice(0, 5)
    .map(([dateKey, groupEvents]) => ({
      dateKey,
      date: parseDate(dateKey),
      items: groupEvents
        .sort((left, right) => String(left.start_time || '').localeCompare(String(right.start_time || '')))
        .map((event) => {
          const noteParts = parseEventNotes(event.notes);
          const subtitle = noteParts.detail || formatCalendarEventTiming(event);

          return {
            id: `${event.id}-${dateKey}`,
            title: noteParts.title,
            subtitle,
          };
        }),
    }));
}

function isNoDismissalDefault(row) {
  return row?.is_no_dismissal === true
    || row?.is_no_dismissal === 1
    || String(row?.is_no_dismissal) === '1';
}

function isSchoolWideDismissalDefault(row) {
  return row && (row.grade === null || row.grade === undefined) && (row.gender === null || row.gender === undefined);
}

export default function SchoolDashboard({
  token,
  navigate,
  schoolId,
  user,
  dismissalActive: dismissalActiveProp,
  onDismissalActiveChange,
  onStartDismissal,
  onEndDismissal,
  bare = false,
}) {
  const [localDismissalActive, setLocalDismissalActive] = useState(false);
  const [calendarEvents, setCalendarEvents] = useState([]);
  const [calendarMonth, setCalendarMonth] = useState(() => startOfMonth(new Date()));
  const [matchingStudentId, setMatchingStudentId] = useState(null);
  const [matchingStaff, setMatchingStaff] = useState(null);
  const [reviewContactRows, setReviewContactRows] = useState([]);
  const [reviewStudentRows, setReviewStudentRows] = useState([]);
  const [reviewMatchesLoading, setReviewMatchesLoading] = useState(false);
  const [reviewMatchesError, setReviewMatchesError] = useState('');
  const [school, setSchool] = useState(null);
  const [gradeSplits, setGradeSplits] = useState([]);
  const [campuses, setCampuses] = useState([]);
  const [carpoolLines, setCarpoolLines] = useState([]);
  const [dismissalDefaults, setDismissalDefaults] = useState([]);
  const [now, setNow] = useState(() => new Date());
  const [todayKey, setTodayKey] = useState(() => formatDateKey(new Date()));
  const [lockdownActive, setLockdownActive] = useState(false);
  const [lockdownModalOpen, setLockdownModalOpen] = useState(false);
  const [lockdownPending, setLockdownPending] = useState(false);
  const dismissalActive = dismissalActiveProp ?? localDismissalActive;
  const {
    stats,
    queuePreviewItems,
    pickupStudents,
    alerts,
    overview,
    hasActivity,
    students,
    parents,
    staff,
    gradeNames,
    gradeLabelMap,
    activeSchedule,
    refresh,
  } = useSchoolDismissalLiveData({ schoolId, token });
  const simulatedDismissalRunning = isDismissalSimulationRunning(schoolId);
  const liveDismissalActive = dismissalActive || hasActivity || simulatedDismissalRunning;
  const selectedAlertStudent = useMemo(
    () => students.find((student) => Number(student.id) === Number(matchingStudentId)) || null,
    [matchingStudentId, students]
  );
  const selectedAlertStaff = useMemo(
    () => {
      if (!matchingStaff) return null;
      return staff.find((member) => Number(member.id) === Number(matchingStaff.id)) || matchingStaff;
    },
    [matchingStaff, staff]
  );
  const selectedReviewMatches = useMemo(
    () => buildQueuedContactMatches(selectedAlertStudent, reviewContactRows, reviewStudentRows),
    [reviewContactRows, reviewStudentRows, selectedAlertStudent]
  );

  const setDismissalActive = (nextValue) => {
    if (onDismissalActiveChange) {
      onDismissalActiveChange(nextValue);
      return;
    }
    setLocalDismissalActive(nextValue);
  };

  useEffect(() => {
    if (!onDismissalActiveChange) return;

    const nextActive = hasActivity || simulatedDismissalRunning;
    if (nextActive !== dismissalActive) {
      onDismissalActiveChange(nextActive);
    }
  }, [dismissalActive, hasActivity, onDismissalActiveChange, simulatedDismissalRunning]);

  const pct = stats.total ? Math.round((stats.dismissed / stats.total) * 100) : 0;
  const todayWeekday = WEEKDAY_CODES[parseDate(todayKey)?.getDay() ?? new Date().getDay()];

  useEffect(() => {
    const interval = setInterval(() => {
      setTodayKey(formatDateKey(new Date()));
    }, 60000);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    let cancelled = false;

    Promise.all([
      apiFetch(`/schools/${schoolId}`, {}, token).catch(() => ({ data: null })),
      apiFetch(`/school-grade-splits?school_id=${schoolId}&is_active=true`, {}, token).catch(() => ({ data: [] })),
      apiFetch(`/campuses/school/${schoolId}`, {}, token).catch(() => ({ data: [] })),
      apiFetch('/carpool-lines', {}, token).catch(() => ({ data: [] })),
      apiFetch(`/dismissal-time-defaults/active?school_id=${schoolId}&date=${todayKey}&weekday=${todayWeekday}`, {}, token).catch(() => ({ data: [] })),
    ])
      .then(([schoolRes, gradeSplitsRes, campusesRes, linesRes, defaultsRes]) => {
        if (cancelled) return;
        const data = schoolRes.data || null;
        setSchool(data);
        setGradeSplits(gradeSplitsRes.data || []);
        setCampuses(campusesRes.data || []);
        setCarpoolLines(linesRes.data || []);
        setDismissalDefaults(defaultsRes.data || []);
        const active = Boolean(Number(data?.lockdown_active));
        setLockdownActive(active);
        if (active) setLockdownModalOpen(true);
      })
      .catch(() => {
        if (!cancelled) {
          setSchool(null);
          setGradeSplits([]);
          setCampuses([]);
          setCarpoolLines([]);
          setDismissalDefaults([]);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [schoolId, todayKey, todayWeekday, token]);

  const schoolCarpoolLines = useMemo(() => {
    const campusIds = new Set(campuses.map((campus) => Number(campus.campus_id)));
    return carpoolLines.filter((line) => campusIds.has(Number(line.campus_id)));
  }, [campuses, carpoolLines]);

  const noDismissalToday = useMemo(
    () => dismissalDefaults.some((row) => isSchoolWideDismissalDefault(row) && isNoDismissalDefault(row)),
    [dismissalDefaults]
  );

  const earliestDismissalTime = useMemo(
    () => {
      if (noDismissalToday) return null;

      return getEarliestTimeOfDay([
        getEarliestEffectiveDismissalTime({ school, lines: schoolCarpoolLines }),
        ...dismissalDefaults
          .filter((item) => !isNoDismissalDefault(item))
          .map((item) => item.dismissal_time),
      ]);
    },
    [dismissalDefaults, noDismissalToday, school, schoolCarpoolLines]
  );

  useEffect(() => {
    if (!selectedAlertStudent) {
      setReviewContactRows([]);
      setReviewStudentRows([]);
      setReviewMatchesLoading(false);
      setReviewMatchesError('');
      return undefined;
    }

    let cancelled = false;
    setReviewMatchesLoading(true);
    setReviewMatchesError('');

    const contactRequests = REVIEW_CONTACT_STATUSES.map((status) => (
      apiFetch(`/import-review?school_id=${schoolId}&status=${status}&record_type=contact`, {}, token)
    ));

    Promise.all([
      ...contactRequests,
      apiFetch(`/import-review?school_id=${schoolId}&status=IMPORTED&record_type=student`, {}, token),
    ])
      .then((responses) => {
        if (cancelled) return;

        const contactResponses = responses.slice(0, REVIEW_CONTACT_STATUSES.length);
        const importedStudentResponse = responses[REVIEW_CONTACT_STATUSES.length];

        setReviewContactRows(contactResponses.flatMap((res) => res.data || []));
        setReviewStudentRows(importedStudentResponse.data || []);
      })
      .catch((err) => {
        if (cancelled) return;
        setReviewContactRows([]);
        setReviewStudentRows([]);
        setReviewMatchesError(err.message || 'Failed to check import review queue');
      })
      .finally(() => {
        if (!cancelled) setReviewMatchesLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [schoolId, selectedAlertStudent, token]);

  useEffect(() => {
    if (!schoolId) return undefined;
    const socket = io(SOCKET_URL);

    socket.on('connect', () => {
      socket.emit('watch-school', schoolId);
    });
    socket.on('school-lockdown-changed', (payload = {}) => {
      if (payload.school_id && Number(payload.school_id) !== Number(schoolId)) return;
      const active = Boolean(payload.lockdown_active);
      setLockdownActive(active);
      if (active) setLockdownModalOpen(true);
    });

    return () => {
      socket.disconnect();
    };
  }, [schoolId]);

  /**
   * The next run, not the first one of the day.
   *
   * A school with a 12:15 half-day line and a 14:45 main line used to count
   * down to 12:15 and then sit on "Past scheduled start" all afternoon, because
   * this worked from a single earliest time. `describeNextCarpool` reads the
   * resolved schedule and picks the soonest line still ahead, so the afternoon
   * run gets its own countdown once the midday one has gone.
   */
  const dismissalCountdown = useMemo(() => {
    if (activeSchedule) {
      const next = describeNextCarpool(activeSchedule);
      if (next.live) return { label: next.label, detail: next.detail, tone: 'live' };
      if (['No dismissal today', 'No dismissal time set', 'Done for today', '—'].includes(next.label)) {
        return { label: next.label, detail: next.detail, tone: 'idle' };
      }
      // A clock time plus "in 2 hours 10 minutes". Name the run only when the
      // school has more than one, where "which one?" is the actual question.
      const manyLines = (activeSchedule.lines || []).length > 1;
      return {
        label: next.label,
        detail: next.detail,
        tone: 'upcoming',
        lineName: manyLines ? next.lineName : null,
      };
    }

    // The schedule endpoint is unreachable; fall back to the single earliest
    // time rather than showing nothing.
    const timeStr = earliestDismissalTime;
    if (!timeStr) return null;

    const diffSeconds = secondsUntilSchoolTime(school, timeStr, now);
    if (diffSeconds === null) return null;
    if (diffSeconds <= 0) return { label: formatTime(timeStr), detail: 'started', tone: 'idle' };

    const totalMinutes = Math.floor(diffSeconds / 60);
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    return {
      label: formatTime(timeStr),
      detail: `in ${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`,
      tone: 'upcoming',
    };
  }, [activeSchedule, earliestDismissalTime, now, school]);

  useEffect(() => {
    if (liveDismissalActive || !earliestDismissalTime) return undefined;
    const interval = setInterval(() => setNow(new Date()), 30000);
    return () => clearInterval(interval);
  }, [earliestDismissalTime, liveDismissalActive]);

  useEffect(() => {
    let cancelled = false;

    apiFetch(`/dismissal-events?school_id=${schoolId}`, {}, token)
      .then((res) => {
        if (!cancelled) {
          setCalendarEvents(res.data || []);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setCalendarEvents([]);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [schoolId, token]);

  useEffect(() => {
    const today = parseDate(todayKey);
    if (today) setCalendarMonth(startOfMonth(today));
  }, [schoolId, todayKey]);

  const calendarDays = useMemo(() => buildCalendarDays(calendarMonth), [calendarMonth]);
  const eventDateMap = useMemo(() => buildEventDateMap(calendarEvents), [calendarEvents]);
  const monthScheduleGroups = useMemo(
    () => buildDashboardScheduleGroups(calendarEvents, calendarMonth),
    [calendarEvents, calendarMonth]
  );
  const activeCalendarDate = todayKey;

  const setLockdownOnServer = async (nextActive) => {
    setLockdownPending(true);
    try {
      const res = await apiFetch(`/schools/${schoolId}`, {
        method: 'PUT',
        body: JSON.stringify({ lockdown_active: nextActive ? 1 : 0 }),
      }, token);
      const updated = res.data;
      const active = Boolean(Number(updated?.lockdown_active));
      setLockdownActive(active);
      setSchool((current) => (current ? { ...current, lockdown_active: updated?.lockdown_active ?? (nextActive ? 1 : 0) } : current));
      return active;
    } finally {
      setLockdownPending(false);
    }
  };

  const handleActivateLockdown = async () => {
    try {
      await setLockdownOnServer(true);
    } catch (err) {
      console.error('Failed to activate lockdown:', err);
    }
  };

  const handleEndLockdown = async () => {
    try {
      await setLockdownOnServer(false);
      setLockdownModalOpen(false);
    } catch (err) {
      console.error('Failed to end lockdown:', err);
    }
  };

  const handleQuickAction = (actionKey) => {
    if (actionKey === 'start-dismissal') {
      if (onStartDismissal) {
        onStartDismissal(schoolId);
        return;
      }

      setDismissalActive(true);
    }

    if (actionKey === 'end-dismissal') {
      if (onEndDismissal) {
        onEndDismissal(schoolId);
        return;
      }

      setDismissalActive(false);
    }

    if (actionKey === 'emergency-lockdown') {
      setLockdownModalOpen(true);
    }
  };

  const isQuickActionDisabled = (actionKey) => {
    if (actionKey === 'start-dismissal') return liveDismissalActive || lockdownActive;
    if (actionKey === 'end-dismissal') return !liveDismissalActive;
    return false;
  };

  const handleAlertClick = (alert) => {
    if (alert?.kind === 'student-parent') {
      setMatchingStaff(null);
      setMatchingStudentId(alert.studentId);
      return;
    }

    if (alert?.kind === 'staff-grades') {
      setMatchingStudentId(null);
      setMatchingStaff(alert.staffRecord || staff.find((member) => Number(member.id) === Number(alert.staffId)) || null);
    }
  };

  const handleAssignParentsToStudent = async (parentIds) => {
    if (!selectedAlertStudent || !parentIds.length) return;

    await Promise.all(parentIds.map(async (parentId) => {
      const parent = parents.find((item) => Number(item.id) === Number(parentId));
      if (!parent) return;

      const nextChildrenIds = [...new Set([
        ...parseChildrenIds(parent.children_ids),
        Number(selectedAlertStudent.id),
      ])];

      await apiFetch(`/contacts/${parent.id}`, {
        method: 'PUT',
        body: JSON.stringify({ children_ids: nextChildrenIds }),
      }, token);
    }));

    const currentGuardians = parseChildrenIds(
      selectedAlertStudent.guardians_permanent_authorized_driver
      || selectedAlertStudent['guardians/permanent_authorized_driver']
      || []
    );
    const nextGuardians = [...new Set([
      ...currentGuardians,
      ...parentIds.map(Number),
    ])];

    await apiFetch(`/students/${selectedAlertStudent.id}`, {
      method: 'PUT',
      body: JSON.stringify({ guardians_permanent_authorized_driver: nextGuardians }),
    }, token);

    await refresh();
    setMatchingStudentId(null);
  };

  const handleAssignGradesToStaff = async (gradeCodes, gradeSplitIds = []) => {
    if (!selectedAlertStaff || (!gradeCodes.length && !gradeSplitIds.length)) return;

    await apiFetch(`/staff/${selectedAlertStaff.id}`, {
      method: 'PUT',
      body: JSON.stringify({
        school_id: selectedAlertStaff.school_id,
        f_name: selectedAlertStaff.f_name,
        l_name: selectedAlertStaff.l_name,
        school_email: selectedAlertStaff.school_email || null,
        mobile_phone: selectedAlertStaff.mobile_phone,
        position_title: selectedAlertStaff.position_title || null,
        permission_level: selectedAlertStaff.permission_level,
        is_active: Number(Boolean(selectedAlertStaff.is_active)),
        assigned_grades: selectedAlertStaff.permission_level === 'TEACHER'
          ? gradeCodes
          : parseAssignedGrades(selectedAlertStaff.assigned_grades),
        assigned_grade_split_ids: selectedAlertStaff.permission_level === 'TEACHER'
          ? gradeSplitIds
          : parseAssignedGradeSplitIds(selectedAlertStaff.assigned_grade_split_ids),
      }),
    }, token);

    await refresh();
    setMatchingStaff(null);
  };

  const content = (
    <>
      <div className="sd-grid">
        <div className="sd-dashboard-col sd-dashboard-col-left">
          <div className={`sd-card sd-dismissal-status ${liveDismissalActive ? 'is-active' : 'is-inactive'}`}>
            <div className="sd-card-header">
              <h3>Dismissal Status</h3>
              {liveDismissalActive ? (
                <span className="sd-live-badge"><span className="sd-live-dot"></span> LIVE</span>
              ) : (
                <span className="sd-status-flag">INACTIVE</span>
              )}
            </div>
            {liveDismissalActive ? (
              <>
                <p className="sd-stat-highlight">
                  <span className="sd-stat-green">{stats.dismissed}</span> / {stats.total} students dismissed
                </p>
                <span className="sd-pct">{pct}%</span>
                <div className="sd-progress-bar">
                  <div className="sd-progress-fill" style={{ width: `${pct}%` }}></div>
                </div>
                <div className="sd-stat-row">
                  <div className="sd-stat-item">
                    <div className="sd-stat-label">Students Remaining</div>
                    <div className="sd-stat-value">{stats.remaining}</div>
                  </div>
                  <div className="sd-stat-item">
                    <div className="sd-stat-label">Cars in Queue</div>
                    <div className="sd-stat-value">{stats.carsInQueue}</div>
                  </div>
                  <div className="sd-stat-item">
                    <div className="sd-stat-label">Ready</div>
                    <div className="sd-stat-value">{stats.ready}</div>
                  </div>
                </div>
              </>
            ) : (
              <>
                <p className="sd-dismissal-inactive-copy">
                  Dismissal has not started
                  {noDismissalToday ? (
                    <>
                      <span className="sd-dismissal-countdown-divider">·</span>
                      <span className="sd-dismissal-countdown">No dismissal today</span>
                    </>
                  ) : dismissalCountdown ? (
                    <>
                      <span className="sd-dismissal-countdown-divider">·</span>
                      <span className={`sd-dismissal-countdown sd-dismissal-countdown-${dismissalCountdown.tone}`}>
                        {dismissalCountdown.tone === 'upcoming'
                          ? (
                            <>
                              Next: <strong>{dismissalCountdown.label}</strong>
                              {dismissalCountdown.lineName ? ` (${dismissalCountdown.lineName})` : ''}
                              {dismissalCountdown.detail ? ` ${dismissalCountdown.detail}` : ''}
                            </>
                          )
                          : <><strong>{dismissalCountdown.label}</strong>{dismissalCountdown.detail ? ` · ${dismissalCountdown.detail}` : ''}</>}
                      </span>
                    </>
                  ) : null}
                </p>
                <div className="sd-dismissal-status-spacer"></div>
                <span className="sd-pct inactive">0%</span>
                <div className="sd-progress-bar inactive"></div>
              </>
            )}
          </div>

          <button
            type="button"
            className="sd-card sd-pickup-zone"
            onClick={() => navigate('pickup-zones', { schoolId, focusBuilder: true })}
          >
            <h3>Pickup Zone</h3>
            <p className="sd-card-sub">Open the zone builder and draw a geofence near the school</p>
            <div className="sd-map-placeholder">
              <div className="sd-map-inner">Map view</div>
            </div>
          </button>

          <div className="sd-card sd-quick-actions">
            <h3>Quick Actions</h3>
            <p className="sd-card-sub">Control dismissal in real time</p>
            <div className="sd-actions-grid">
              {QUICK_ACTIONS.map((action) => {
                const disabled = isQuickActionDisabled(action.key);

                return (
                  <button
                    key={action.key}
                    type="button"
                    className={`sd-action-tile ${action.span === 2 ? 'span-2' : ''} ${action.description ? 'has-detail' : 'compact'} ${disabled ? 'is-disabled' : ''}`}
                    onClick={() => handleQuickAction(action.key)}
                    disabled={disabled}
                  >
                    <img className="sd-action-icon-img" src={action.iconSrc} alt="" aria-hidden="true" />
                    <div className="sd-action-copy">
                      <div className="sd-action-name">{action.label}</div>
                    </div>
                    {action.description ? <div className="sd-action-detail">{action.description}</div> : null}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        <div className="sd-dashboard-col sd-dashboard-col-center">
          <div className="sd-dashboard-toprow">
            <div className="sd-card sd-queue-preview">
              <h3>Queue Preview</h3>
              <p className="sd-card-sub">
                {liveDismissalActive
                  ? (queuePreviewItems.length ? `${queuePreviewItems.length} parents in queue` : 'No parents in queue')
                  : 'No parents in queue'}
              </p>
              {queuePreviewItems.length ? (
                <div className="sd-queue-list">
                  {queuePreviewItems.map((p, i) => (
                    <div key={i} className="sd-queue-item">
                      <SchoolAvatar record={p} seed={p.id ?? i} className="sd-queue-avatar" />
                      <div className="sd-queue-copy">
                        <div className="sd-queue-name">{p.name}</div>
                        <div className="sd-queue-sub">({p.loaded})</div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="sd-card-empty sd-card-empty-toprow">
                  {'Parents will appear here once\nthey arrive on campus'}
                </div>
              )}
              {liveDismissalActive ? (
                <button className="sd-card-btn sd-queue-btn" onClick={() => navigate('dismissal-console', schoolId)}>
                  View Full Queue
                </button>
              ) : null}
            </div>

            <PickupStudentsCard
              title="Sent to Carpool"
              students={pickupStudents}
              subtitle={liveDismissalActive && pickupStudents.length ? undefined : 'No students have been sent to carpool'}
              hint=""
              actionLabel={liveDismissalActive ? 'View Full Queue' : 'Dismissal Inactive'}
              actionDisabled={!liveDismissalActive}
              emptyMessage={'Students will appear here once they are\nsent to carpool'}
              emptyClassName="sd-card-empty-toprow"
              hideActionWhenEmpty
              onActionClick={() => navigate('dismissal-console', schoolId)}
            />
          </div>

          <div className="sd-card sd-calendar">
            <h3>School Calendar</h3>
            <p className="sd-card-sub">View upcoming dismissal changes<br />and school events</p>
            <div className="sd-calendar-shell">
              <div className="sd-calendar-month-panel">
                <div className="sd-calendar-month">{calendarMonth.toLocaleDateString([], { month: 'long' })}</div>
                <div className="sd-calendar-header">
                  {DASHBOARD_WEEK_DAYS.map((day, index) => (
                    <span key={`${day}-${index}`}>{day}</span>
                  ))}
                </div>
                <div className="sd-calendar-days">
                  {calendarDays.map((day) => {
                    const dotCount = Math.min(eventDateMap.get(day.key)?.length || 0, 3);
                    const isActive = day.key === activeCalendarDate;

                    return (
                      <div key={day.key} className={`sd-calendar-day ${day.inMonth ? '' : 'muted'} ${isActive ? 'active' : ''}`}>
                        <span className={`sd-calendar-day-number ${isActive ? 'active' : ''}`}>
                          {day.inMonth ? day.day : ''}
                        </span>
                        <span className="sd-calendar-day-dots" aria-hidden="true">
                          {day.inMonth
                            ? Array.from({ length: dotCount }, (_, index) => (
                              <span key={`${day.key}-dot-${index}`} className="sd-calendar-day-dot"></span>
                            ))
                            : null}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
              <div className="sd-calendar-events">
                {monthScheduleGroups.length === 0 ? (
                  <div className="sd-card-empty sd-card-empty-toprow">
                    {'No calendar events have been\ncreated for this month'}
                  </div>
                ) : monthScheduleGroups.map((group) => (
                  <div key={group.dateKey} className="sd-calendar-event-group">
                    <div className="sd-calendar-event-date">
                      <span className="sd-calendar-event-day">
                        {group.date?.toLocaleDateString([], { weekday: 'short' }) || '—'}
                      </span>
                      <span className="sd-calendar-event-num">{group.date?.getDate() || '—'}</span>
                    </div>
                    <div className="sd-calendar-event-cards">
                      {group.items.map((item) => (
                        <div key={item.id} className="sd-calendar-event-card">
                          <div className="sd-calendar-event-title">{item.title}</div>
                          <div className="sd-calendar-event-subtitle">{item.subtitle}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <button className="sd-card-btn" onClick={() => navigate('calendar', schoolId)}>Open Calendar</button>
          </div>
        </div>

        <div className="sd-dashboard-col sd-dashboard-col-right">
          <div className="sd-card sd-alerts">
            <h3>Alerts</h3>
            <p className="sd-card-sub">{liveDismissalActive ? 'System warnings' : 'Dismissal inactive'}</p>
            {alerts.length ? (
              <>
                <div className="sd-alert-list">
                  {alerts.map((a, i) => (
                    (() => {
                      const actionable = ['student-parent', 'staff-grades'].includes(a.kind);

                      return (
                        <button
                          key={a.id || i}
                          type="button"
                          className={`sd-alert-item ${actionable ? 'is-clickable' : ''}`}
                          onClick={() => handleAlertClick(a)}
                          disabled={!actionable}
                        >
                          <div className="sd-alert-info">
                            <div className="sd-alert-name">{a.name}</div>
                            <div className="sd-alert-grade">{a.grade}</div>
                            <div className="sd-alert-msg">{a.message}</div>
                          </div>
                          <img className="sd-alert-icon" src="/icons/alert.png" alt="" aria-hidden="true" />
                        </button>
                      );
                    })()
                  ))}
                </div>
                <p className="sd-alert-footer">Alerts are automatically cleared once resolved</p>
              </>
            ) : (
              <div className="sd-card-empty sd-alert-empty">
                {liveDismissalActive ? 'No active validation or live alerts' : 'No validation alerts for this school'}
              </div>
            )}
          </div>

          <div className="sd-card sd-todays-overview">
            <h3>Today's Overview</h3>
            <p className="sd-card-sub">{liveDismissalActive ? 'Live dismissal performance summary' : 'Dismissal is inactive'}</p>
            {(stats.liveByLine || []).length > 0 && (
              <div className="sd-line-breakdown">
                {/* One wave per line. The school-wide totals describe both at
                    once, which reads as though half the school is missing while
                    only the early line is running. */}
                {stats.liveByLine.map((line) => (
                  <div key={line.line_id ?? 'unassigned'} className="sd-line-row">
                    <span className="sd-line-name">{line.name}</span>
                    <span className="sd-line-figures">
                      <strong>{line.dismissed} / {line.total}</strong> dismissed
                      <span className="sd-line-sep">·</span>
                      {line.ready} ready
                      <span className="sd-line-sep">·</span>
                      {line.carsInQueue} {line.carsInQueue === 1 ? 'car' : 'cars'}
                    </span>
                  </div>
                ))}
                {stats.combinedLive && (
                  <div className="sd-line-row sd-line-row-total">
                    <span className="sd-line-name">Both lines running</span>
                    <span className="sd-line-figures">
                      <strong>{stats.combinedLive.dismissed} / {stats.combinedLive.total}</strong> dismissed
                      <span className="sd-line-sep">·</span>
                      {stats.combinedLive.ready} ready
                      <span className="sd-line-sep">·</span>
                      {stats.combinedLive.carsInQueue} {stats.combinedLive.carsInQueue === 1 ? 'car' : 'cars'}
                    </span>
                  </div>
                )}
              </div>
            )}

            <div className="sd-overview-stats">
              <div className="sd-overview-item">
                <span className="sd-overview-value sd-green">{stats.dismissed} / {stats.total}</span>
                <span className="sd-overview-label">Students Dismissed</span>
              </div>
              <div className="sd-overview-item">
                <span className="sd-overview-value sd-blue">{overview.averagePickupTime}</span>
                <span className="sd-overview-label">Avg Pickup Time</span>
              </div>
              <div className="sd-overview-item">
                <span className="sd-overview-value sd-orange">{stats.carsInQueue} Cars</span>
                <span className="sd-overview-label">Current Queue</span>
              </div>
              <div
                className="sd-overview-item"
                title="Parents, guardians, and authorized drivers currently inside an active school pickup geofence"
              >
                <span className="sd-overview-value sd-blue">
                  {stats.pickupContactsNearSchool} {stats.pickupContactsNearSchool === 1 ? 'Person' : 'People'}
                </span>
                <span className="sd-overview-label">Parents &amp; Drivers Near School</span>
              </div>
              <div className="sd-overview-item">
                <span className="sd-overview-value sd-red">{overview.longestWait}</span>
                <span className="sd-overview-label">Longest Wait</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <StudentParentMatchModal
        student={selectedAlertStudent}
        parents={parents}
        gradeLabelMap={gradeLabelMap}
        reviewMatches={selectedReviewMatches}
        reviewMatchesLoading={reviewMatchesLoading}
        reviewMatchesError={reviewMatchesError}
        onClose={() => setMatchingStudentId(null)}
        onAssign={handleAssignParentsToStudent}
        onOpenReviewQueue={() => {
          setMatchingStudentId(null);
          navigate('import-review', schoolId);
        }}
      />

      <StaffGradeAssignModal
        staff={selectedAlertStaff}
        gradeNames={gradeNames}
        gradeSplits={gradeSplits}
        groupingMode={school?.student_grouping_mode || 'grades'}
        onClose={() => setMatchingStaff(null)}
        onAssign={handleAssignGradesToStaff}
      />

      {lockdownModalOpen ? (
        <EmergencyLockdownModal
          phase={lockdownActive ? 'active' : 'confirm'}
          onClose={() => setLockdownModalOpen(false)}
          onActivate={handleActivateLockdown}
          onEnd={handleEndLockdown}
          activating={lockdownPending && !lockdownActive}
          ending={lockdownPending && lockdownActive}
        />
      ) : null}
    </>
  );

  if (bare) return content;

  return (
    <SchoolLayout
      token={token} navigate={navigate} schoolId={schoolId} user={user}
      activePage="dashboard" breadcrumb="Dashboard"
      headerRight={(
        <DismissalStateControl
          active={liveDismissalActive}
          onToggle={() => (liveDismissalActive ? handleQuickAction('end-dismissal') : handleQuickAction('start-dismissal'))}
        />
      )}
    >
      {content}
    </SchoolLayout>
  );
}
